// Grafo de dependencias de visibilidad entre categorias (grupos) de un formulario.
//
// Espeja el guard del backend (GroupsController@rulesLogicLeavesGroupHidden, TKT-2480): una categoria
// condicionada a una pregunta que vive dentro de ella misma, o a una cadena circular sin entrada
// externa, nunca se puede mostrar. El llenado evalua las reglas de una categoria con OR
// (formVisibility.js: rules.some), asi que un ciclo CON entrada externa si es satisfacible.
//
// Las dos piezas del grafo llegan de fuentes distintas:
//   - `rulesGroups`: los grupos del store (GET forms/groups/{id}) traen `visibility_rules` pero NO
//     items; de aqui salen las categorias y sus reglas.
//   - `itemGroupMap`: pregunta -> categoria, armado con los items del formulario ya cargado en el
//     builder (`form.groups[]._items`), que son las preguntas VIGENTES. Una regla cuya pregunta no
//     aparece aqui (version no vigente o pregunta borrada) no se puede cumplir en el llenado: deja a
//     su categoria condicionada pero sin conexion satisfacible, no la vuelve incondicional.

function groupItems (group) {
  return group._items || group.items || []
}

export function buildItemGroupMap (formGroups) {
  const map = {}
  ;(formGroups || []).forEach((group) => {
    groupItems(group).forEach((item) => {
      if (item && item.id != null) map[item.id] = group.id
    })
  })
  return map
}

function matchesExcluded (rule, excludeRule) {
  if (!excludeRule) return false
  return rule.depends_on_item_id === excludeRule.itemId &&
    (excludeRule.index == null || rule.index === excludeRule.index)
}

export function buildGroupDependencies (rulesGroups, itemGroupMap, excludeRule) {
  const dependencies = {}
  ;(rulesGroups || []).forEach((group) => {
    ;(group.visibility_rules || []).forEach((rule) => {
      if (matchesExcluded(rule, excludeRule)) return
      // La categoria queda registrada como condicionada aunque el disparador no exista.
      if (!dependencies[group.id]) dependencies[group.id] = {}
      const triggerGroupId = itemGroupMap[rule.depends_on_item_id]
      if (triggerGroupId != null) dependencies[group.id][triggerGroupId] = true
    })
  })
  return dependencies
}

function computeShowable (groupIds, dependencies) {
  const showable = {}
  groupIds.forEach((id) => {
    if (!dependencies[id]) showable[id] = true
  })

  let changed = true
  while (changed) {
    changed = false
    Object.keys(dependencies).forEach((conditioned) => {
      if (showable[conditioned]) return
      if (Object.keys(dependencies[conditioned]).some((trigger) => showable[trigger])) {
        showable[conditioned] = true
        changed = true
      }
    })
  }

  return showable
}

// Devuelve una funcion que responde si condicionar `conditionedGroupId` a una pregunta que vive en
// `triggerGroupId` dejaria alguna categoria sin forma de mostrarse. Se arma una vez el estado actual
// (con la regla que se esta reconfigurando) y la base sin ella; cada consulta simula la regla nueva y
// rechaza si la propia categoria queda oculta o si una categoria hoy mostrable dejaria de serlo (mover
// una regla puede quitarle a otra categoria su unica entrada).
export function makeHiddenChecker (rulesGroups, itemGroupMap, excludeRule) {
  const groupIds = (rulesGroups || []).map((group) => group.id)
  const currentShowable = computeShowable(groupIds, buildGroupDependencies(rulesGroups, itemGroupMap))
  const baseDependencies = buildGroupDependencies(rulesGroups, itemGroupMap, excludeRule)

  return (conditionedGroupId, triggerGroupId) => {
    if (conditionedGroupId == null || triggerGroupId == null) return false

    const dependencies = { ...baseDependencies }
    dependencies[conditionedGroupId] = {
      ...(baseDependencies[conditionedGroupId] || {}),
      [triggerGroupId]: true
    }

    const resultingShowable = computeShowable(groupIds, dependencies)
    if (!resultingShowable[conditionedGroupId]) return true
    return groupIds.some((id) => currentShowable[id] && !resultingShowable[id])
  }
}

// ¿Ofrecer `conditionedGroupId` como destino dejaría una condición que nunca se podría cumplir? La
// categoría propia del disparador nunca es válida (auto-referencia), aunque por el OR del llenado siga
// mostrable por otra regla: el backend la rechaza siempre y el Punto 1 pide no ofrecerla.
export function isTargetGroupDisabled (conditionedGroupId, triggerGroupId, isHidden) {
  if (conditionedGroupId == null || triggerGroupId == null) return false
  return conditionedGroupId === triggerGroupId || isHidden(conditionedGroupId, triggerGroupId)
}
